import React, {useCallback, useEffect, useState} from 'react'
import {
  Agenda,
  AgendaListProps,
} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList'
import {useCardsTemplate} from 'Event/template/Cards'
import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  RemoveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useAutoUpdateSidebarItem} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem'
import {useForm} from 'react-hook-form'
import Settings from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaItemConfig/Settings'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import {generateHashId} from 'lib/crypto/hash'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {Rule} from 'Event/attendee-rules'

export function AgendaItemConfig(
  props: {
    agenda: Agenda
    id?: string
    listId: string
  } & ComponentConfigProps,
) {
  const {showing, onClose, agenda, id, listId} = props
  const {sidebarItems} = useCardsTemplate()
  const {handleSubmit, control, errors, watch, formState} = useForm()
  const saveTemplate = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const removeTranslation = useRemoveTranslations()
  const [rules, setRules] = useState<Rule[]>(agenda.rules || [])

  useEffect(() => {
    if (showing) {
      return
    }

    setRules(agenda.rules || [])
  }, [agenda, showing])

  const update = (id: string, updated: Agenda) => {
    const text = replaceAtPath(updated, 'text', `{{${id}_text}}`)
    const link = replaceAtPath(updated, 'link', `{{${id}_link}}`)
    saveTemplate({
      sidebarItems: {
        [listId]: {
          items: {[id]: updated},
        },
      },
      localization: addTranslation({
        [`${id}_text`]: text ?? '',
        [`${id}_link`]: link ?? '',
      }),
    })
  }

  const insert = (item: Agenda) => {
    const list = sidebarItems[listId] as AgendaListProps
    const position = Object.keys(list.items).length + 1

    generateHashId([
      'agenda_item',
      new Date().valueOf().toString(),
      Math.random().toString(),
    ]).then((id) => {
      const componentId = `agenda_${id}`
      saveTemplate({
        sidebarItems: {
          [listId]: {
            items: {
              [componentId]: {
                ...item,
                position,
                text: `{{${componentId}_text}}`,
                link: `{{${componentId}_link}}`,
              },
            },
          },
        },
        localization: addTranslation({
          [`${componentId}_text`]: item.text ?? '',
          [`${componentId}_link`]: item.link ?? '',
        }),
      })
    })
  }

  const remove = useCallback(() => {
    if (!id) {
      throw new Error("Missing 'id' of agenda to remove.")
    }

    saveTemplate({
      sidebarItems: {
        [listId]: {
          items: {[id]: REMOVE},
        },
      },
      localization: removeTranslation([`${id}_text`, `${id}_link`]),
    })

    onClose()
  }, [id, saveTemplate, listId, removeTranslation, onClose])

  useAutoUpdateSidebarItem({
    item: id ? {items: {[id]: watch()}} : {},
    when: showing && Boolean(id),
  })

  const save = (formData: Agenda) => {
    const data: Agenda = {
      ...formData,
      rules,
    }

    if (id === undefined) {
      insert(data)
      onClose()
      return
    }

    update(id, data)
    onClose()
  }

  return (
    <ComponentConfig
      showing={showing}
      onClose={onClose}
      title="Agenda"
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <Settings
          control={control}
          agenda={agenda}
          watch={watch}
          errors={errors}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={setRules} />
      <Footer>
        <SaveButton />
        <RemoveButton
          aria-label="remove agenda"
          onClick={remove}
          showing={Boolean(id)}
        />
      </Footer>
    </ComponentConfig>
  )
}
